<template>
  <div v-if="hasErrors" class="error-messages">
    <ul>
      <li v-for="error in errors" :key="error.code">
        {{ error.message }}
      </li>
    </ul>
  </div>

  <form class="form" @submit.prevent="handleFormSubmit">
    <TextField
      :value="firstname"
      :validation="v$.firstname"
      autocomplete="given-name"
      label="First name"
      @input="(value) => (firstname = value)"
    />

    <TextField
      :value="lastname"
      :validation="v$.lastname"
      autocomplete="family-name"
      label="Last name"
      @input="(value) => (lastname = value)"
    />

    <TextField
      :value="email"
      :validation="v$.email"
      autocomplete="email"
      type="email"
      label="E-mail address"
      @input="(value) => (email = value)"
    />

    <TextField
      :value="password"
      :validation="v$.password"
      autocomplete="new-password"
      type="password"
      label="Password"
      @input="(value) => (password = value)"
    />

    <TextField
      :value="confirmPassword"
      :validation="v$.confirmPassword"
      autocomplete="new-password"
      type="password"
      label="Confirm Password"
      @input="(value) => (confirmPassword = value)"
    />

    <template v-if="isPhoneNumberInputEnabled">
      <label class="field field--checkbox">
        <span class="field__label">Text me with news and offers</span>
        <div class="input input--checkbox">
          <input
            v-model="showPhoneNumberInput"
            type="checkbox"
            class="input__element input__element--checkbox"
          />
          <span class="input__checkbox"></span>
        </div>
      </label>

      <template v-if="showPhoneNumberInput">
        <div class="telephone-wrapper">
          <label class="field">
            <div class="input">
              <select
                class="input__element"
                :value="usersCountryCode"
                @change="handleCodeChange"
              >
                <option
                  v-for="code in telephoneCodesOrdered"
                  :key="code.code"
                  :value="code.code"
                >
                  {{ code.dial_code }}
                </option>
              </select>
              <div v-if="v$.selectedDialCode.$dirty" class="input__response">
                <ul>
                  <li
                    v-for="error of v$.selectedDialCode.$errors"
                    :key="error.$uid"
                  >
                    {{ error.$message }}
                  </li>
                </ul>
              </div>
            </div>
          </label>

          <TextField
            :value="telephone"
            :validation="v$.telephone"
            autocomplete="tel"
            type="tel"
            label="Phone number"
            @input="(value) => (telephone = value)"
          />
        </div>
        <div class="legal">
          By signing up via text, you agree to receive recurring automated
          marketing messages, including cart reminders, at the phone number
          provided. Consent is not a condition of purchase. Reply STOP to
          unsubscribe. Reply HELP for help.
        </div>
      </template>
    </template>

    <div class="field">
      <Button type="submit" width="full" theme="primary" :loading="loading">
        Create account
      </Button>
    </div>
  </form>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required, email, sameAs, numeric } from "@vuelidate/validators";
import { mapActions, mapGetters } from "vuex";
import isEmpty from "lodash-es/isEmpty";
import Button from "@/components/ButtonV3.vue";
import telephoneCodes from "@/assets/json/telephone-codes.json";
import { regionFeatureToggles } from "@/config.js";
import {
  orderTelephoneCodes,
  dialCodeForCountry,
  formatFullTelephoneNumber,
  fetchUserCountryCode,
} from "@/helpers/telephone.js";
import TextField from "@/components/TextField.vue";

export default {
  name: "RegisterForm",
  components: {
    Button,
    TextField,
  },
  emits: ["success"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      confirmPassword: "",
      telephone: "",
      codes: telephoneCodes,
      selectedDialCode: null,
      usersCountryCode: null,
      showPhoneNumberInput: false,
      errors: [],
      loading: false,
    };
  },
  computed: {
    ...mapGetters("app", ["currencyCode"]),
    hasErrors() {
      return this.errors.length > 0;
    },
    fullTelephoneNumber() {
      return formatFullTelephoneNumber(this.selectedDialCode, this.telephone);
    },
    telephoneCodesOrdered() {
      return orderTelephoneCodes(this.codes);
    },
    isPhoneNumberInputEnabled() {
      return (
        regionFeatureToggles[this.currencyCode]?.phoneNumberSignUp || false
      );
    },
  },
  watch: {
    showPhoneNumberInput(val) {
      val && this.preSelectUsersTelephoneCode();
    },
  },
  methods: {
    ...mapActions("auth", ["attemptRegister"]),
    handleCodeChange(event) {
      const countryCode = event.target.value;
      this.usersCountryCode = countryCode;
      this.selectedDialCode = dialCodeForCountry(this.codes, countryCode);
    },
    async preSelectUsersTelephoneCode() {
      try {
        const usersCountryCode = await fetchUserCountryCode();
        if (!usersCountryCode) {
          return;
        }

        this.usersCountryCode = usersCountryCode;
        this.selectedDialCode = dialCodeForCountry(
          this.codes,
          usersCountryCode
        );
      } catch {
        // Geolocation lookup failed; user can pick a dial code manually.
      }
    },
    async handleSMSNotification() {
      const updateProfileResponse = await fetch(
        `${process.env.RATANDBOA_NODE_APP_URL}/klaviyo/sms-list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: this.email,
            phone_number: this.fullTelephoneNumber,
          }),
        }
      );

      if (!updateProfileResponse.ok) {
        this.success = false;
        return false;
      }

      return true;
    },
    async handleFormSubmit() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      if (this.showPhoneNumberInput && this.isPhoneNumberInputEnabled) {
        this.v$.telephone.$touch();
        this.v$.selectedDialCode.$touch();

        if (this.v$.telephone.$invalid || this.v$.selectedDialCode.$invalid) {
          return;
        }
      }

      let registerInput = {
        firstName: this.firstname,
        lastName: this.lastname,
        email: this.email,
        password: this.password,
      };

      if (this.showPhoneNumberInput && this.isPhoneNumberInputEnabled) {
        registerInput = {
          ...registerInput,
          phone: this.fullTelephoneNumber,
        };
      }

      this.loading = true;
      const data = await this.attemptRegister(registerInput);

      if (data.customerUserErrors.length >= 1) {
        this.errors = data.customerUserErrors;
      } else {
        if (this.showPhoneNumberInput && this.isPhoneNumberInputEnabled) {
          await this.handleSMSNotification();
        }

        if (!isEmpty(data.customerAccessToken)) {
          this.$emit("success");
        }
      }

      this.loading = false;
    },
  },
  validations() {
    let validations = {
      firstname: { required },
      lastname: { required },
      email: { required, email },
      password: { required },
      confirmPassword: { sameAsPassword: sameAs(this.password) },
    };

    if (this.showPhoneNumberInput) {
      validations = {
        ...validations,
        telephone: { required, numeric },
        selectedDialCode: { required },
      };
    }

    return validations;
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.error-messages {
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: $color-red;
  padding: 16px;
  color: $color-white;
  border-radius: 2px;
  font-size: 10px;
}

.legal {
  font-size: 11px;
  margin: 0 0 30px 0;
}
</style>
